import { FC } from 'react';
import { Properties } from 'csstype';

// ==================== Константы ====================

// Размеры столбцов
export const COL_GROUP_MINWIDTH = 260;
export const COL_WIDTH = 120;
export const COL_MIN_WIDTH = 120;
export const COL_MAX_WIDTH = 900;

// Размеры таблицы
export const HEADER_HEIGHT = 110;
export const TABLE_MIN_WIDTH = 1000;
export const TABLE_MIN_HEIGHT = 300;
export const TABLE_HEIGHT_OFFSET = 150;
export const CELL_HEIGHT = 38;

// Тайминги
export const EXPAND_DELAY_MS = 1000;
export const DEBOUNCE_RESIZE_MS = 100;

// Магические строки
export const AD_GROUP_NAME = 'Реклама';
export const AD_KEY_PREFIX = '0-0';
export const TOTAL_PATH_STR = 'total';
export const TOTAL_CHOSEN_PATH_STR = 'total_choosen';
export const TOTAL_LABEL = 'Итого и среднее';
export const EMPTY_GROUP = 'empty';

// ==================== Типы ====================

export type ReportType = 
    | 'universal'      // un-table (регистрации)
    | 'order'          // order-table (заказы)
    | 'payment'        // payment-table (платежи)
    | 'ad'             // ad-table (реклама)
    | 'webinar'        // web-table (вебинары)
    | 'visit';         // visit-table (визиты)

// ==================== Интерфейсы данных ====================

export interface MetricItem {
    dataKey: string;
    name: string;
    label?: string;
    description?: string;
    key?: string;
    background?: string;
    plan?: number;
    base_metric_name?: string;
    metric_formula?: string;
    id?: number;
    visual_parameters?: string;
    custom_metric_name?: string;
    constant_argument?: number;
    [key: string]: unknown;
}

export interface GroupingItem {
    dataKey: string;
    name: string;
    label?: string;
    key?: string;
    [key: string]: unknown;
}

export interface TableRowData {
    pathStr: string;
    pathStr2: string;
    group: string;
    parentId?: string;
    uniqueKey?: string;
    children?: TableRowData[];
    clientData?: {
        path: string[];
        downloaded?: boolean;
        loading?: boolean;
    };
    [key: string]: unknown;
}

export interface TableData {
    data: TableRowData[];
    total: Record<string, number>;
    totalChosen: {
        active: string[];
        metrics: Record<string, number>;
    };
}

export interface MaxValuesMap {
    [dataKey: string]: {
        maxPositive: number;
        maxNegative: number;
    };
}

export interface ColumnConfig {
    title: string;
    dataKey: string;
    key: string;
    width: number;
    minWidth: number;
    maxWidth: number;
    frozen?: string | boolean;
    resizable: boolean;
    groupings?: GroupingItem[];
    sortTable?: Record<string, unknown>;
    filtersTable?: Record<string, unknown>;
    isFilling?: boolean;
    loadRowKeys?: string[];
    columnWidth?: number;
    columnsWidth?: Record<string, number>;
    description?: string;
    background?: string;
    item?: MetricItem;
    plan?: number;
    base_metric_name?: string;
    metric_formula?: string;
    headerRenderer: (props: { column?: ColumnConfig }) => React.ReactNode;
    cellRenderer: (renderItem: CellRenderProps) => React.ReactNode;
}

export interface CellRenderProps {
    cellData: unknown;
    rowData: TableRowData;
    rowIndex: number;
    column: ColumnConfig;
}

// ==================== Props интерфейсы ====================

/**
 * Режим отображения таблицы.
 * - 'full' — полный режим (страница отчёта)
 * - 'compact' — компактный режим (блок дашборда)
 * - 'preview' — превью (модальное окно)
 */
export type TableDisplayMode = 'full' | 'compact' | 'preview';

export interface ReportTableProps {
    /** Тип отчёта */
    reportType: ReportType;
    /** Скрыть группы с персональными данными */
    hidePersonalDataGroups?: boolean;
    /** ID блока для дашбордов */
    blockId?: string;
    /** Режим отображения */
    displayMode?: TableDisplayMode;
    /** Режим модального окна (для работы с dashboardModal state) */
    isModal?: boolean;
    /** Дата блока дашборда (формат: "type_start_end") */
    date?: string;
    /** Переопределить период (игнорировать глобальный период из Redux) */
    overridePeriod?: { start: string; end: string; withTime?: boolean };
    /** Максимальное количество строк (для compact/preview режимов) */
    maxRows?: number;
    /** Callback для открытия полного отчёта */
    onOpenFullReport?: () => void;
    /** Скрыть панель группировок */
    hideGroupingsPanel?: boolean;
    /** Скрыть панель метрик */
    hideMetricsPanel?: boolean;
    /**
     * Окно блока дашборда: места в его собственной панели «Группировки / Метрики», куда
     * таблица дорисует кнопки, как в отчёте — выгрузку (left), «Динамику», заливку и копирование (right)
     */
    toolbarSlots?: { left?: HTMLElement | null; right?: HTMLElement | null };
    /** Окно блока-таблицы: в заголовках столбцов спарклайн и «…» → «Дополнительно», хотя hideMetricButtons */
    showTableSettings?: boolean;
    /**
     * Отдельное место для строк таблицы (state.table.blocks[id].tableData), при том что настройки
     * (группировки, метрики, фильтры) общие. Предпросмотр в окне блока: иначе он и таблица окна
     * за 7 дней перезаписывают строки друг друга
     */
    dataSlotId?: string;
    /** Имя файла выгрузки по умолчанию (окно блока — название блока) */
    getExportFileName?: () => string;
    /** Высота таблицы (по умолчанию автоматическая) */
    tableHeight?: number;
    /** Скрыть элементы выбора строк (Выбрать все, Первые N) */
    hideSelectionControls?: boolean;
    /** Скрыть чекбокс "Итого и среднее" */
    hideTotalCheckbox?: boolean;
    /** Скрыть кнопки метрик (график и т.д.) */
    hideMetricButtons?: boolean;
    /** Скрыть строку "Итого и среднее выбранных" */
    hideTotalChosenRow?: boolean;
    /** Скрыть чекбоксы выбора строк группировок */
    hideRowCheckboxes?: boolean;
    /** Отключить ограничение на выбор групп (для типа "Таблица") */
    disableGroupsLimit?: boolean;
    /** Кастомные колонки */
    customColumns?: ColumnConfig[];
    /** Кастомный рендерер ячеек */
    customCellRenderer?: (props: CellRenderProps) => React.ReactNode;
    /** Callback при загрузке данных */
    onDataLoad?: (data: TableRowData[]) => void;
    /** Callback при раскрытии строки */
    onRowExpand?: (row: TableRowData) => void;
    /** Callback при изменении сортировки */
    onSortChange?: (sort: Record<string, 'ASC' | 'DESC'>) => void;
    /** Callback при изменении фильтров */
    onFiltersChange?: (filters: Record<string, unknown>) => void;
    /** Внешнее управление загрузкой */
    externalLoading?: boolean;
    /** Внешние данные (для переопределения Redux) */
    externalData?: TableRowData[];
    /** Ширина внешнего контейнера блока (для горизонтального скролла в compact-режиме) */
    blockContainerWidth?: number;
}

export interface GroupingsComponentProps {
    reportType?: ReportType;
    isDisable?: boolean;
    hidePersonalDataGroups?: boolean;
    adsGroupsSelected?: boolean;
    adsGroupsAvailable?: boolean;
    setAdsGroupsAvailable?: (value: boolean) => void;
    setAdsGroupsSelected?: (value: boolean) => void;
}

export interface MetricsComponentProps {
    reportType?: ReportType;
    isDisable?: boolean;
    adsGroupsSelected?: boolean;
    adsGroupsAvailable?: boolean;
    setAdsGroupsAvailable?: (value: boolean) => void;
    setAdsGroupsSelected?: (value: boolean) => void;
}

export interface ReportConfig {
    GroupingsComponent: FC<GroupingsComponentProps>;
    MetricsComponent: FC<MetricsComponentProps>;
    tableId: string;
    groupingsTooltip: string;
    metricsTooltip: string;
    tableTooltip: string;
    hasAdsGroups: boolean;
}

// ==================== Утилитарные типы ====================

export interface FillingStyleParams {
    maxValues: MaxValuesMap;
    dataKey: string;
    cellValue: number;
    isNotTotalRow: boolean;
}

export interface CreateColumnsParams {
    columnWidth: number;
    metrics: MetricItem[];
    groupings: GroupingItem[];
    sortTable: Record<string, unknown>;
    filtersTable: Record<string, unknown>;
    frozensColumns: Record<string, boolean>;
    loadRowKeys: string[];
    expandedRowKeys: string[];
    columnsWidth: Record<string, number>;
    isFilling: boolean;
    isCompare: boolean;
    handleCellWidthChange: (width: number) => void;
    handleAllRowsCheck: (pathStrings: string[]) => void;
    setFrozensColumns: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
    renderNormalCell: (renderItem: CellRenderProps, dataKey: string, item: MetricItem) => React.ReactNode;
    renderFillingCell: (renderItem: CellRenderProps, dataKey: string, item: MetricItem) => React.ReactNode;
}
